import { reactive, computed } from "vue"
import { defineStore } from "pinia"
import axios, { type AxiosRequestConfig } from "axios"
import { isNullish } from "@tinyurldev/tu-ui-vue"

import useLegacyStore from "@Composables/useLegacyStore"
import useNotify from "@Composables/useNotify.js"
import confirmPassword from "@Helpers/confirmPassword"
import { ParseServerError } from "@Modules/shared/Api/ServerErrors"
import UserModel, {
  createGuestUser,
  type User,
  type UserData,
  type UserSubscriptionsData,
} from "../Data/Models/UserModel"
import type { DomainData } from "@Modules/urls/Data/Models/DomainTypes"
import SUBSCRIPTION_TYPE from "@Constants/subscription-type"

export interface AuthUser {
  id: string
  name: string
  email: string
  photo_url: string
  subscriptions?: UserSubscriptionsData
  has_subscription_history?: boolean
  isDeveloper?: boolean | null;
  uses_two_factor_auth?: number
  active_two_factor_backup_codes_count?: number
  facebook_id?: string | null
  google_id?: string | null
  twitter_id?: string | null
  microsoft_id?: string | null
  domains?: {
    owned: DomainData[]
  }
  email_verified_at?: Date | null
  created_at?: string,
  vat_id?: string | null
  status?: number | null
}

export interface UpdateContactParams {
  name: string
  email: string
}

/**
 * Minimal structural type for the deprecated Vuex store bridge returned by
 * useLegacyStore() — only the commit method is used here.
 */
interface LegacyStore {
  commit(type: string, payload?: unknown): void
}

interface AuthState {
  user: AuthUser | null
  ccpaRequired: boolean
  gdprRequired: boolean
  locale: string
  impersonated?: boolean | null
  preferredBillingType: number | null
  collectsEuropeanVat: boolean
  errors: Record<string, string>
  loading: {
    contact: boolean
    photo: boolean
  }
}

/**
 * The XAuthStore pinia store is used to store information about the authenticated user.
 */
export const XAuthStore = defineStore("x-auth", () => {
  const { notifySuccess, notifyError } = useNotify()

  //
  // Data
  const state = reactive<AuthState>({
    user: null,
    ccpaRequired: false,
    gdprRequired: false,
    locale: "en",
    preferredBillingType: null,
    collectsEuropeanVat: false,
    errors: {},
    loading: {
      contact: false,
      photo: false,
    },
  })

  //
  // Getters
  const user = computed(() => state.user)
  const errors = computed(() => state.errors)
  const loading = computed(() => state.loading)

  /**
   * Returns the user as a User smart object with subscription data.
   * Always returns a User object (never null) - returns GUEST_USER when not logged in.
   */
  const userModel = computed<User>(() => {
    if (!state.user) {
      return createGuestUser()
    }
    // Convert AuthUser to UserData format for the model
    const userData: UserData = {
      id: state.user.id,
      name: state.user.name,
      email: state.user.email,
      photo_url: state.user.photo_url,
      subscriptions: state.user.subscriptions,
      has_subscription_history: state.user.has_subscription_history,
      uses_two_factor_auth: state.user.uses_two_factor_auth,
      active_two_factor_backup_codes_count: state.user.active_two_factor_backup_codes_count,
      facebook_id: state.user.facebook_id,
      google_id: state.user.google_id,
      twitter_id: state.user.twitter_id,
      microsoft_id: state.user.microsoft_id,
      status: state.user.status,
    }
    return UserModel(userData)
  })

  /**
   * Returns true if the user is logged in.
   */
  const isLoggedIn = computed(() => userModel.value.isLoggedIn())

  /**
   * Returns true if the user's email is verified.
   */
  const isEmailVerified = computed(() => !!state.user?.email_verified_at)

  /**
   * Returns true if CCPA consent is required.
   */
  const ccpaRequired = computed(() => state.ccpaRequired)

  /**
   * Returns true if GDPR consent is required.
   */
  const gdprRequired = computed(() => state.gdprRequired)

  /**
   * Returns the user's owned domains.
   */
  const userOwnedDomains = computed<DomainData[]>(() => state.user?.domains?.owned ?? [])

  /**
   * Returns the billing type the user is billed through.
   *
   * It is the billing type of the default subscription, falling back to the
   * billing type the application prefers when the user has no subscription.
   */
  const billingType = computed<number | null>(() => {
    const defaultSubscription = state.user?.subscriptions?.current?.find(
      (subscription) => subscription.type === SUBSCRIPTION_TYPE.DEFAULT,
    )

    return defaultSubscription?.billing_type ?? state.preferredBillingType
  })

  /**
   * Returns true when the application charges European VAT, which is what decides
   * whether the billing page asks for a VAT id at all.
   */
  const collectsEuropeanVat = computed<boolean>(() => state.collectsEuropeanVat)

  /**
   * Returns the user's locale for formatting purposes.
   */
  const locale = computed<string>(() => state.locale)
  const isImpersonated = computed(() => !isNullish(state.impersonated) && state.impersonated);

  //
  // Actions

  /**
   * Initialize the store with user data from the application state
   * @param userData      The user data from sparkState
   * @param ccpaRequired  Whether CCPA consent is required
   * @param gdprRequired  Whether GDPR consent is required
   * @param locale        The user's locale for formatting
   * @param impersonated  Indicates whether the user is impersonated or not.
   * @param preferredBillingType The billing type the application bills new subscriptions through
   * @param collectsEuropeanVat Whether the application charges European VAT
   */
  const initializeFromState = (userData: AuthUser | null, ccpaRequired = false, gdprRequired = false, locale = "en", impersonated = false, preferredBillingType: number | null = null, collectsEuropeanVat = false) => {
    state.user = userData
    state.ccpaRequired = ccpaRequired
    state.gdprRequired = gdprRequired
    state.locale = locale
    state.impersonated = impersonated
    state.preferredBillingType = preferredBillingType
    state.collectsEuropeanVat = collectsEuropeanVat
  }

  /**
   * Refresh the application state by calling the Vuex refreshState action.
   * This maintains compatibility with the existing Vuex-based state management.
   * Note: requires a Vue component instance context (uses getCurrentInstance).
   */
  const refreshApplicationState = async () => {
    const store = useLegacyStore()
    if (store) {
      await store.dispatch("refreshState")
    }
  }

  /**
   * Refresh the application state by fetching the state endpoint directly and
   * dispatching to the Vuex store. Does not require a Vue component instance
   * context, so it can be called from modals or other contexts where
   * getCurrentInstance() returns null.
   */
  const refreshUserState = async () => {
    const { data } = await axios.get("/app/api/state")
    const store = (await import("@Store/index.js")).default
    store.dispatch("refreshState", data)
  }

  /**
   * Clear all validation errors
   */
  const clearErrors = () => {
    state.errors = {}
  }

  /**
   * Update user profile in both XAuthStore and Vuex sparkState
   * @param updates The user properties to update
   * @param legacyStore Optional Vuex store instance for updating legacy state
   */
  const updateUserProfile = (updates: Partial<AuthUser>, legacyStore?: LegacyStore) => {
    // Update XAuthStore state
    if (state.user) {
      state.user = {
        ...state.user,
        ...updates,
      }
    }

    // Update Vuex sparkState if store was provided
    if (legacyStore) {
      legacyStore.commit("UPDATE_USER_PROFILE", updates)
    }
  }

  /**
   * Update the user's contact information (name and email)
   * @param params The contact information to update
   * @returns True if the update was successful, false otherwise
   */
  const updateContact = async (params: UpdateContactParams, legacyStore?: LegacyStore): Promise<boolean> => {
    state.loading.contact = true
    state.errors = {}

    try {
      // DISABLE_ERROR_INTERCEPTORS is an app-specific flag read by the axios plugin (see plugins/axios.js)
      const config: AxiosRequestConfig & { DISABLE_ERROR_INTERCEPTORS?: boolean } = {
        DISABLE_ERROR_INTERCEPTORS: true,
      }
      await axios.put("/app/api/settings/contact", params, config)
      updateUserProfile({
        name: params.name,
        email: params.email,
      }, legacyStore)
      notifySuccess("Your contact information has been updated.")
      return true
    } catch (error) {
      const { status, fieldErrors } = ParseServerError(error)
      if (status === 422) {
        const errors: Record<string, string> = {}
        Object.entries(fieldErrors ?? {}).forEach(([key, val]) => {
          errors[key] = val[0] ?? ""
        })
        state.errors = errors
      } else if (status === 423) {
        state.loading.contact = false
        const isConfirmed = await confirmPassword()
        if (isConfirmed) {
          return updateContact(params, legacyStore)
        }
      } else {
        notifyError()
      }
      return false
    } finally {
      state.loading.contact = false
    }
  }

  /**
   * Update the user's profile photo
   * @param photo The photo file to upload
   * @param legacyStore Optional Vuex store instance for updating legacy state
   * @returns True if the update was successful, false otherwise
   */
  const updatePhoto = async (photo: File, legacyStore?: LegacyStore): Promise<boolean> => {
    state.loading.photo = true
    state.errors = {}

    try {
      const formData = new FormData()
      formData.append("photo", photo)
      await axios.post("/app/api/settings/photo", formData)
      // Update user in both XAuthStore and Vuex with local object URL
      updateUserProfile({
        photo_url: URL.createObjectURL(photo),
      }, legacyStore)
      notifySuccess("Your profile photo has been updated.")
      return true
    } catch (error) {
      const { status, fieldErrors } = ParseServerError(error)
      if (status === 422) {
        const errors: Record<string, string> = {}
        Object.entries(fieldErrors ?? {}).forEach(([key, val]) => {
          errors[key] = val[0] ?? ""
        })
        state.errors = errors
        notifyError({ text: errors.photo })
      } else {
        notifyError()
      }
      return false
    } finally {
      state.loading.photo = false
    }
  }

  return {
    // Getters
    user,
    userModel,
    isLoggedIn,
    isEmailVerified,
    ccpaRequired,
    gdprRequired,
    userOwnedDomains,
    billingType,
    collectsEuropeanVat,
    locale,
    errors,
    loading,
    isImpersonated,
    // Actions
    initializeFromState,
    refreshApplicationState,
    refreshUserState,
    clearErrors,
    updateContact,
    updatePhoto,
  }
})
